import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X, ChevronsUpDown, Settings, LogOut, Check, CircleHelp, Search, type LucideIcon } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { useUnreadNotifications } from "@/hooks/useUnreadNotifications";
import { useSiteSelector } from "@/hooks/useSiteSelector";
import { toast } from "sonner";
import { FAB_INSET, FAB_SIZE, PANEL_MARGIN, Z, EXPAND_SPRING, COLLAPSE_TRANSITION, CONTENT_FADE } from "@/lib/overlay-constants";
import type { SidebarNavItem } from "@/config/navigation";
import ConcretlyLogo from "@/assets/concretly-logo.svg";

export interface MobileDrawerNavCTA {
  icon: LucideIcon;
  label: string;
  onClick: () => void;
  variant?: "default" | "secondary";
}

interface MobileDrawerNavProps {
  navItems: SidebarNavItem[];
  ctas: MobileDrawerNavCTA[];
  settingsPath: string;
  showSiteSelector?: boolean;
}

export function MobileDrawerNav({
  navItems,
  ctas,
  settingsPath,
  showSiteSelector = false,
}: MobileDrawerNavProps) {
  const [open, setOpen] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const { unreadCount } = useUnreadNotifications();
  const { sites, selectedSite, selectSite } = useSiteSelector();

  const companyName = user?.company_name || "Mon entreprise";
  const userName = `${user?.first_name || ""} ${user?.last_name || ""}`.trim() || user?.email?.split("@")[0] || "Utilisateur";
  const userEmail = user?.email || "";

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open]);

  // Close on route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const isActive = (url: string, exact?: boolean) => {
    if (exact) return location.pathname === url;
    return (
      location.pathname === url ||
      location.pathname === url + "/" ||
      location.pathname.startsWith(url + "/")
    );
  };

  const navigateTo = (url: string) => {
    setOpen(false);
    navigate(url);
  };

  const handleSiteSelect = (site: { id: string; name: string; location: string; status?: string }) => {
    selectSite(site);
    setOpen(false);
    if (showSiteSelector) {
      navigate("/site");
    } else {
      navigate(`/sites/${site.short_code ?? site.id}`);
    }
  };

  const handleSignOut = async () => {
    setIsLoggingOut(true);
    try {
      const { error } = await signOut();
      if (error) {
        toast.error("Erreur lors de la déconnexion");
      } else {
        toast.success("Déconnexion réussie");
      }
      navigate("/auth");
    } catch {
      toast.error("Erreur lors de la déconnexion");
      navigate("/auth");
    } finally {
      setIsLoggingOut(false);
      setShowLogoutDialog(false);
      setOpen(false);
    }
  };

  return (
    <>
      {/* Burger trigger — fixed bottom-left */}
      <button
        ref={triggerRef}
        onClick={() => setOpen(!open)}
        aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
        className={cn(
          "fixed flex items-center justify-center rounded-full bg-background transition-colors",
          open
            ? "text-foreground border-transparent"
            : "text-muted-foreground border border-border hover:bg-muted",
        )}
        style={{
          left: FAB_INSET,
          bottom: `calc(${FAB_INSET}px + env(safe-area-inset-bottom, 0px))`,
          width: FAB_SIZE,
          height: FAB_SIZE,
          zIndex: open ? Z.fabActive : Z.fabIdle,
        }}
      >
        <AnimatePresence mode="wait" initial={false}>
          {open ? (
            <motion.div
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              <X className="w-6 h-6" />
            </motion.div>
          ) : (
            <motion.div
              key="menu"
              initial={{ rotate: 90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: -90, opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              <Menu className="w-6 h-6" />
            </motion.div>
          )}
        </AnimatePresence>
      </button>

      {/* Expanded panel — grows from trigger */}
      {createPortal(
        <AnimatePresence>
          {open && (
            <>
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="fixed inset-0 bg-black/40"
                style={{ zIndex: Z.backdrop }}
                onClick={() => setOpen(false)}
              />

              {/* Panel */}
              <motion.div
                ref={panelRef}
                className="fixed flex flex-col rounded-lg border border-border bg-background overflow-hidden"
                style={{ zIndex: Z.panel }}
                initial={{
                  left: FAB_INSET,
                  bottom: `calc(${FAB_INSET}px + env(safe-area-inset-bottom, 0px))`,
                  width: FAB_SIZE,
                  height: FAB_SIZE,
                  borderRadius: 32,
                  opacity: 0.8,
                }}
                animate={{
                  left: PANEL_MARGIN,
                  right: PANEL_MARGIN,
                  bottom: `calc(${PANEL_MARGIN}px + env(safe-area-inset-bottom, 0px))`,
                  width: `calc(100vw - ${PANEL_MARGIN * 2}px)`,
                  height: "auto",
                  borderRadius: 12,
                  opacity: 1,
                  transition: EXPAND_SPRING,
                }}
                exit={{
                  left: FAB_INSET,
                  bottom: `calc(${FAB_INSET}px + env(safe-area-inset-bottom, 0px))`,
                  width: FAB_SIZE,
                  height: FAB_SIZE,
                  borderRadius: 32,
                  opacity: 0,
                  transition: COLLAPSE_TRANSITION,
                }}
              >
                {/* Content — fades in after expand */}
                <motion.div {...CONTENT_FADE}>
                  {/* Header — Concretly logo + company name dropdown */}
                  <div className="px-lg pt-lg pb-xs">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="flex items-center gap-md w-full px-sm py-sm rounded-lg hover:bg-muted transition-colors">
                          <img
                            src={ConcretlyLogo}
                            alt="Concretly"
                            className="h-6 w-6 shrink-0"
                          />
                          <div className="grid flex-1 text-left leading-tight overflow-hidden">
                            <span className="text-label text-foreground truncate">
                              {companyName}
                            </span>
                            <span className="text-body-sm text-muted-foreground truncate">
                              {showSiteSelector
                                ? (selectedSite?.name || "Aucun chantier")
                                : companyName}
                            </span>
                          </div>
                          <ChevronsUpDown className="ml-auto size-4 shrink-0 text-muted-foreground" />
                        </button>
                      </DropdownMenuTrigger>

                      <DropdownMenuContent
                        className="w-[calc(100vw-4rem)] min-w-56 rounded-lg"
                        style={{ zIndex: Z.dropdown }}
                        side="bottom"
                        align="start"
                        sideOffset={4}
                      >
                        <DropdownMenuGroup>
                          <div className="px-sm py-2xs text-body-sm text-muted-foreground">Connecté en tant que</div>
                          <div className="px-sm pb-2xs">
                            <div className="text-label text-foreground truncate">{userName}</div>
                            <div className="text-helper text-muted-foreground truncate">{userEmail}</div>
                          </div>
                        </DropdownMenuGroup>

                        {showSiteSelector && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuGroup>
                              <DropdownMenuLabel>Chantier actif</DropdownMenuLabel>
                              {sites.map((site) => (
                                <DropdownMenuItem
                                  key={site.id}
                                  onClick={() => handleSiteSelect(site)}
                                  className="gap-sm"
                                >
                                  <span className="flex-1 truncate">{site.name}</span>
                                  {selectedSite?.id === site.id && (
                                    <Check className="size-4 shrink-0 text-muted-foreground" />
                                  )}
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuGroup>
                          </>
                        )}

                        <DropdownMenuSeparator />

                        <DropdownMenuGroup>
                          <DropdownMenuItem onClick={() => navigateTo(showSiteSelector ? "/site/help" : "/help")}>
                            <CircleHelp className="mr-2 h-4 w-4" />
                            Aide
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigateTo(settingsPath)}>
                            <Settings className="mr-2 h-4 w-4" />
                            Paramètres
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => {
                              setOpen(false);
                              window.dispatchEvent(
                                new KeyboardEvent("keydown", { key: "k", metaKey: true }),
                              );
                            }}
                          >
                            <Search className="mr-2 h-4 w-4" />
                            Recherche
                            <kbd className="ml-auto pointer-events-none inline-flex h-5 select-none items-center gap-3xs rounded border border-border bg-background px-2xs font-mono text-body-sm text-muted-foreground">
                              <span className="text-body-sm">⌘</span>K
                            </kbd>
                          </DropdownMenuItem>
                        </DropdownMenuGroup>

                        <DropdownMenuSeparator />

                        <DropdownMenuGroup>
                          <DropdownMenuItem
                            onClick={() => setShowLogoutDialog(true)}
                            variant="destructive"
                          >
                            <LogOut className="mr-2 h-4 w-4" />
                            Se déconnecter
                          </DropdownMenuItem>
                        </DropdownMenuGroup>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  {/* Nav items — big action buttons */}
                  <nav className="grid grid-cols-2 gap-sm p-lg pt-sm">
                    {navItems.map((item) => {
                      const Icon = item.icon;
                      const active = isActive(item.url, item.exact);
                      const isNotif = item.title === "Notifications";

                      return (
                        <button
                          key={item.url}
                          onClick={() => navigateTo(item.url)}
                          className={cn(
                            "flex flex-col items-center justify-center gap-sm px-md py-lg rounded-lg border text-center transition-colors",
                            active
                              ? "bg-accent border-border text-accent-foreground font-medium"
                              : "border-border/50 text-muted-foreground hover:bg-muted hover:text-foreground",
                          )}
                        >
                          <div className="relative">
                            <Icon className="w-6 h-6" />
                            {isNotif && unreadCount > 0 && (
                              <span className="absolute -top-2xs -right-2.5 flex items-center justify-center min-w-4 h-4 px-3xs text-[10px] leading-none text-destructive-foreground bg-destructive rounded-full">
                                {unreadCount > 99 ? "99+" : unreadCount}
                              </span>
                            )}
                          </div>
                          <span className="text-body-sm">{item.title}</span>
                        </button>
                      );
                    })}
                  </nav>

                  {/* CTA buttons — icon-only, aligned right with gap for close button */}
                  {ctas.length > 0 && (
                    <div className="flex justify-end gap-sm p-lg pt-sm">
                      {ctas.map((cta) => {
                        const CtaIcon = cta.icon;
                        return (
                          <Button
                            key={cta.label}
                            variant={cta.variant || "default"}
                            size="icon"
                            className="h-14 w-14 rounded-lg"
                            onClick={() => {
                              cta.onClick();
                              setOpen(false);
                            }}
                            title={cta.label}
                            aria-label={cta.label}
                          >
                            <CtaIcon className="!w-6 !h-6" />
                          </Button>
                        );
                      })}
                    </div>
                  )}
                </motion.div>
              </motion.div>
            </>
          )}
        </AnimatePresence>,
        document.body,
      )}

      {/* Logout confirmation */}
      <AlertDialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la déconnexion</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir vous déconnecter ?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isLoggingOut}>
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleSignOut} disabled={isLoggingOut}>
              {isLoggingOut ? "Déconnexion..." : "Se déconnecter"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
